import { useEffect, useState, useContext } from 'react';
import Layout from '@/components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, ArrowLeft, Trash2 } from 'lucide-react';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABELS = {
  'basic': 'BASIC PRACTICE',
  'comps_ready': "COMP'S READY",
  'advanced': 'ADVANCED'
};

export default function JeopardyCategoryPage() {
  const { tier, categorySlug } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [games, setGames] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [tier, categorySlug]);

  const loadData = async () => {
    try {
      // Load all jeopardy games and filter by tier and category
      const [gamesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/jeopardy-games`),
        axios.get(`${API}/test-categories`) // Public endpoint
      ]);
      
      // Find the category
      const foundCategory = categoriesRes.data.find(cat => cat.slug === categorySlug);
      setCategory(foundCategory);
      
      // Filter games for this tier and category
      const filtered = gamesRes.data.filter(
        g => g.tier === tier && g.category === categorySlug
      );
      setGames(filtered);
    } catch (error) {
      console.error('Failed to load jeopardy games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGameClick = (gameId) => {
    navigate(`/jeopardy-game/${gameId}`);
  };

  const handleBackClick = () => {
    navigate('/jeopardy-center');
  };

  const handleDelete = async (gameId, title) => {
    if (!window.confirm(`Delete Jeopardy game "${title || 'this game'}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/jeopardy-games/${gameId}`, { withCredentials: true });
      setGames(prev => prev.filter(g => g.id !== gameId));
      toast.success('Jeopardy game deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete game');
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div 
        className="relative bg-gradient-to-br from-orange-400 via-yellow-500 to-orange-500 h-[300px] mb-12 flex items-center justify-center"
      >
        <div className="container mx-auto px-6">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white hover:text-[#F4E4C1] transition-colors mb-6"
          >
            <ArrowLeft className="w-6 h-6" />
            <span className="text-lg font-bold">Back to Jeopardy Center</span>
          </button>
          <div className="text-center">
            <div className="text-6xl mb-4">{category?.icon || '🏆'}</div>
            <h1 className="text-5xl md:text-6xl retro-title text-white mb-2">
              {category?.name.toUpperCase() || categorySlug.toUpperCase()}
            </h1>
            <p className="text-xl text-white font-bold mb-2">
              {TIER_LABELS[tier] || tier.toUpperCase()}
            </p>
            <p className="text-lg text-white opacity-90">
              {games.length} Jeopardy Game{games.length !== 1 ? 's' : ''} Available
            </p>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="container mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-2xl text-[#6B4423]">Loading games...</div>
          </div>
        ) : games.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div key={game.id} className="relative" data-testid={`jeopardy-game-card-${game.id}`}>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(game.id, game.test_title); }}
                    className="absolute top-3 right-3 z-10 bg-[#E85D4D] text-white p-2 rounded-full hover:bg-[#d14a3b] transition-colors shadow-lg"
                    title="Delete this Jeopardy game"
                    data-testid={`delete-jeopardy-button-${game.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleGameClick(game.id)}
                  className="w-full bg-gradient-to-br from-orange-100 to-yellow-100 border-4 border-[#FFA500] p-8 rounded-lg text-left transition-all transform hover:scale-105 hover:shadow-2xl"
                >
                  <div className="flex items-start justify-between mb-4">
                    <Trophy className="w-10 h-10 text-[#FF8C00]" />
                    <span className="bg-[#FFA500] text-white px-3 py-1 rounded-full text-sm font-bold">
                      {game.questions?.length || 20} Questions
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#6B4423] mb-3 retro-title">
                    {game.test_title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {game.game_categories?.length || 4} categories • Answer in question form • Score up to {(game.game_categories?.length || 4) * 1500} points!
                  </p>
                  <div className="text-xs text-gray-500">
                    Created {new Date(game.created_at).toLocaleDateString()}
                  </div>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <div className="text-2xl text-gray-600 mb-2">No Jeopardy games in this category yet</div>
            <p className="text-gray-500">Check back later or try another category!</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
